import {ServiceBase} from "../ServiceBase";

export enum AnimatedIconUnloadReason {
    CONTEXT_LOST,
    VIEWPORT_EXIT,
    REQUESTED,
    GAME_OPEN
}

export interface IAnimatedIconRequestOptions {
    jsonUrl: string;
    wrapper: HTMLElement;
    size: {width: number; height: number};
    timings: {delay: number; interval: number;};
    onAnimationLoad?(): void;
    onAnimationUnload?(reason: AnimatedIconUnloadReason): void;
    onAnimationBeforeUnload?(reason: AnimatedIconUnloadReason): void;
}

export type AnimatedIconObservableTarget = IAnimatedIconRequestOptions & {
    animationDisposer?(reason: AnimatedIconUnloadReason): void;
    inViewport?: boolean;
    loadingInProgress?: boolean;
    abortLoading?: boolean;
};


export class AnimatedIconBaseService extends ServiceBase{
    protected attemptDisposeAnimation(observedTarget: AnimatedIconObservableTarget, reason: AnimatedIconUnloadReason) {
        if (observedTarget.animationDisposer) {
            if (observedTarget.onAnimationBeforeUnload) {
                observedTarget.onAnimationBeforeUnload(reason);
            }
            observedTarget.animationDisposer(reason);
            observedTarget.animationDisposer = undefined;
            if (observedTarget.onAnimationUnload) {
                observedTarget.onAnimationUnload(reason);
            }
        } if (observedTarget.loadingInProgress) {
            // we cannot actually abort loading the resources (json, png, atlas) because they are xhr calls
            // so we set this flag and after the xhr is done we do not start the animation and we reset the flag
            observedTarget.abortLoading = true;
        }
    }

    protected async loadAnimatedIcon(options: AnimatedIconObservableTarget, resourceName: string) {
        if (options.loadingInProgress) {
            throw new Error("Animated Icon Loading in progress already for this icon. Please be patient");
        }
        try {
            options.loadingInProgress = true;
            let {app: pixiApp, dispose: disposePixiApp, PixiSpine, canvas} = await this.services.pixiApp.createPixiApp({
                width: options.size.width, height: options.size.height,
                onContextLost: () => {
                    this.attemptDisposeAnimation(options, AnimatedIconUnloadReason.CONTEXT_LOST);
                }
            });
            canvas.style.display = "block";
            canvas.style.width = "100%";

            const {resources} = await this.services.pixiApp.loadResources(pixiApp, [{name: resourceName, jsonUrl: options.jsonUrl}]).catch((e: unknown) => {
                disposePixiApp();
                throw e;
            });

            let spineAnimation = new PixiSpine.Spine(resources[resourceName].spineData);

            // get the animations
            const availableAnimations = resources[resourceName].spineData.animations.map(a => a.name);

            // play animation
            const runAnimation = () => {
                // animated icons will have only one animation that we play in loop. There won't be complex timeline animations
                spineAnimation.state.setAnimation(0, availableAnimations[0], false);
            }
            let animationSchedule: number;

            spineAnimation.state.addListener({
                complete: (entry) => {
                    // when animation completes, schedule the next iteration
                    animationSchedule = setTimeout(runAnimation, options.timings.interval);
                }
            });

            runAnimation();

            const animationDisposer = () => {
                clearTimeout(animationSchedule);
                spineAnimation.destroy();
                animationSchedule = undefined;
                spineAnimation = undefined;
                disposePixiApp();
            };

            if (options.abortLoading) {
                animationDisposer();
                options.abortLoading = false;
            } else {
                options.animationDisposer = animationDisposer;
                // add the animation to the scene and render...
                pixiApp.stage.addChild(spineAnimation);
                options.wrapper.appendChild(pixiApp.view);

                if (options.onAnimationLoad) {
                    options.onAnimationLoad();
                }
            }
            options.loadingInProgress = false;

        } catch (e) {
            options.loadingInProgress = false;
            throw e;
        }
    }
}